import {
   AfterContentChecked,
   ChangeDetectorRef,
   Component,
   Directive,
   ElementRef,
   HostListener, InjectionToken,
   Injector, Input,
   OnDestroy,
   OnInit,
   Renderer2
} from '@angular/core';

import { Location, LocationStrategy, PathLocationStrategy } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { UpgradeComponent } from '@angular/upgrade/static';
import { Store } from '@ngrx/store';
import * as moment from 'moment-timezone';
import { Observable, Subscription } from 'rxjs/index';
import { filter } from 'rxjs/operators';
import { AuthenticationActions } from './auth/state/authentication.actions';
import { AuthState } from './auth/state/authentication.reducer';
import { AuthenticationSelectors } from './auth/state/authentication.selectors';
import { UpgradedProviderService } from './hybrid/upgraded-provider.service';
import { CustomThemeService } from './services/custom-theme.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    providers: [Location, {provide: LocationStrategy, useClass: PathLocationStrategy}] // HashLocationStrategy
})
export class AppComponent implements OnInit, OnDestroy, AfterContentChecked {
    title = 'app';

    private subscription: Subscription = new Subscription();
    public authenticate$: Observable<AuthState>;
    isPortalVersionChanged$: Observable<any>;
    isInIframe: boolean;

    constructor(private authenticationSelectors: AuthenticationSelectors,
                private store: Store<any>,
                public upgradedProviderService: UpgradedProviderService,
                public router: Router,
                private route: ActivatedRoute,
                private location: Location,
                private changeDetector: ChangeDetectorRef,
                private customThemeService: CustomThemeService,
                private renderer: Renderer2
                // private locationStrategy: LocationStrategy
    ) {
        // At this point the router.url is just / (in the view template router.url is showing correct path
        // If for example the url is https://backend.autoremind.us/appointments
        // then location.path() and locationStrategy.path() are both /appointments

        // If the URL is starting with #/ (eg. https://backend.autoremind.us/#/appointments?personid=123) then
        // this.location.path() is empty and locationStrategy.path() is just /

        // console.log('URL:', router.url);
        // console.log('this.location.path', this.location.path());
        // console.log('this.locationStrategy.path()', this.locationStrategy.path());
        this.redirectIfUrlWithHash();

        // Any subscriptions to router.events registered BEFORE navigating will emit on first routing event before navigating to new url
        // Any subscriptions after navigating will only catch routing events after that

        // console.log('Setting up streams');

        this.authenticate$ = this.authenticationSelectors.selectAuthenticate();
        this.isPortalVersionChanged$ = this.authenticationSelectors.selectIsPortalVersionChangedSelector();

        // console.log('Streams ready');
        this.customThemeService.setTheme('bs3');

        this.isInIframe = window.self !== window.top;

        // Ensure styling for FrontDesk
        if (window.self !== window.top) {
           const link = this.renderer.createElement('link');
           link.rel = 'stylesheet';
           link.href = 'https://fonts.googleapis.com/css2?family=Source+Sans+3:wght@400;500;600;700&display=swap';
           this.renderer.appendChild(document.head, link);
           document.documentElement.classList.add('in-iframe'); // Add to <html> for global access
           document.body.classList.add('in-iframe'); // Add explicitly to <body>
       }

    }

    ngOnInit(): void {
        if (this.isInIframe) {

           window.addEventListener('message', event => {
                if (!event.origin.includes('localhost') && !event.origin.includes('autoremind.us')) {
                   return;
                }

                if (event.data.route) {
                    this.router.navigateByUrl(event.data.route);
                }
            });
        }

        this.location.onUrlChange((url: string, state: unknown) => {
            console.log('Location changes to url:', url, ', state:', state);
        });

        this.subscription.
        add(
            this.authenticate$
                .pipe
                    (filter(authenticate =>  authenticate.user !== undefined))
                .subscribe(authenticate => {
                        // TODO: Consider moving this to an NgRx effect
                        if (authenticate.user.userTimeZone !== 'US/Pacific-New') {
                            moment.tz.setDefault(authenticate.user.userTimeZone);
                        } else {
                            moment.tz.setDefault('America/Los_Angeles');
                        }
                        if (this.isInIframe) {
                            window.parent.postMessage({ reAuthenticatedLegacyUser: false }, '*'); // change it to exact url
                        }
                    }
                )
        );
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
       // console.log('AppComponent.onDestroy()');
    }

    // check if browser is closed, if yes - logout so all running tasks in BE can be killed
    @HostListener('window:beforeunload', ['$event']) beforeunloadHandler(event): void {
        this.store.dispatch(AuthenticationActions.windowBeforeUnload());
    }

    /**
     * If browsing to the portal with an old URL including a # (e.g. https://backend.autoremind.us/#/appointments)
     * then remove the hash and update the URL.
     * If called in AppComponent's constructor it should (hopefully) not affect any other components or the angularJS
     * part. A navigation cycle is fired both before and after changing the route. However, as long as components
     * only subscribe after this method is called they should only catch the events after changing the URL.
     */
    redirectIfUrlWithHash(): void {
       // console.log('redirectIfUrlWithHash(): window.location.hash', window.location.hash);
        let newPath = window.location.hash;

        newPath = newPath.replace('#', '');
        if (newPath !== window.location.hash) {
         //   console.log('Redirecting to URL without #');
            const params = {};
            const hash = newPath.split('?');
            if (hash.length > 1) {
                const search = hash[1].split('&');
                for (const pair of search) {
                    const itemArray = pair.split('=');
                    params[itemArray[0]] = itemArray[1];
                }
            }
            // console.log('HERE');
            this.router.navigate([hash[0]], {queryParams: params});
         //   console.log('redirectIfUrlWithHash(): After router.navigate');
        }
        // console.log('THERE');
    }

    ngAfterContentChecked(): void {
        this.changeDetector.detectChanges();
    }

    reload(): void {
        window.location.reload();
    }

}

// This was an attempt to avoid deprected injector.get('$rootScope')
const ROOTSCOPE = new InjectionToken<ng.IRootScopeService>('$rootScope');

// This Angular directive will act as an interface to the "upgraded" AngularJS component.
@Directive({selector: 'ng1-message-details-report'})
export class Ng1MessageDetailsReportComponentWrapperDirective extends UpgradeComponent {
    // The names of the input and output properties here must match the names of the
    // `<` and `&` bindings in the AngularJS component that is being wrapped.
    @Input() jobId !: String;

    constructor(elementRef: ElementRef, injector: Injector) {
        // We must pass the name of the directive as used by AngularJS to the super.
        super('messagingDetailsReport', elementRef, injector);
    }
}
